/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type * as RadixUIDialog from "@radix-ui/react-dialog";
import { Close as RadixUIDialogClose } from "@radix-ui/react-dialog";
import type { ComponentProps, ReactNode } from "react";

import {
  numericPercentages,
  spacing,
  type ThemeType,
} from "../../../../../global/emotion";
import { useIsDesktopViewport } from "../../../../hooks/useIsDesktopViewport";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import type { ScrollAreaProps } from "../../../blocks/other/ScrollArea/ScrollArea.emotion";
import { ScrollArea } from "../../../blocks/other/ScrollArea/ScrollArea.emotion";
import { IconButton } from "../../_core/IconButton/IconButton.emotion";
import { Text } from "../../_core/Text/Text.emotion";
import { CloseIcon } from "../../icons/Icon/NavigationIcons";
import type { FullscreenModalProps } from "../FullscreenModal/FullscreenModal.emotion";
import { FullscreenModal } from "../FullscreenModal/FullscreenModal.emotion";
import type { ModalProps } from "../Modal/Modal.emotion";
import { Modal } from "../Modal/Modal.emotion";

/**
 * The shared frame for both the desktop and the fullscreen modal. Composed
 * *before* the caller's `contentCSS` in both, so a caller can override any of it
 * — the 480px desktop width in particular.
 */
const contentContainerStyle: Interpolation<ThemeType> = (theme) => ({
  gap: spacing.none,
  display: "flex",
  flexDirection: "column",
  padding: `${spacing.l} ${spacing.none} ${spacing.none} ${spacing.none}`,
  maxWidth: numericPercentages[100],
  width: numericPercentages[100],
  color: theme.semanticColors.primary,
  [cssMediaQueryMinWidthTablet]: {
    maxHeight: "95dvh",
    maxWidth: "480px",
    padding: `${spacing.xl} ${spacing.none} ${spacing.none} ${spacing.none}`,
    width: "480px",
    display: "flex",
    flexDirection: "column",
  },
});

const scrollAreaViewportStyle: Interpolation = {
  height: "100%",
  minHeight: 0,
  padding: `${spacing.none} ${spacing.xl} ${spacing.none} ${spacing.xl}`,
  [cssMediaQueryMinWidthTablet]: {
    padding: `${spacing.none} ${spacing.xl} ${spacing.none} ${spacing.xl}`,
  },
};

const scrollAreaRootStyle: Interpolation = {
  height: "100%",
  minHeight: 0,
};

const footerStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.onOverlayBackground,
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  padding: `${spacing.s} ${spacing.xl} ${spacing.l} ${spacing.xl}`,
  [cssMediaQueryMinWidthTablet]: {
    backgroundColor: theme.semanticColors.onOverlayBackground,
  },
});

const headerStyle: Interpolation = {
  display: "flex",
  justifyContent: "space-between",
  flexShrink: 0,
  padding: `${spacing.none} ${spacing.xl}`,
  alignItems: "center",
};

const headerStyleNoTitle: Interpolation = {
  justifyContent: "flex-end",
};

export type StickyFooterModalProps = ComponentProps<
  typeof RadixUIDialog.Root
> & {
  trigger?: ReactNode;
  fullScreenModalProps?: FullscreenModalProps;
  modalProps?: ModalProps;
  title?: string;
  footerChildren?: ReactNode;
  customHeader?: ReactNode;
  footerCSS?: Interpolation;
  scrollAreaProps?: Pick<
    ScrollAreaProps,
    | "showScrollbarWhenScrollable"
    | "showScrollbarsOnHover"
    | "masksColor"
    | "dependencies"
  >;
};

export const StickyFooterModal = ({
  modalProps,
  fullScreenModalProps = {
    showCloseButton: false,
  },
  children,
  trigger,
  title,
  footerChildren,
  customHeader,
  footerCSS,
  scrollAreaProps,
  ...rootProps
}: StickyFooterModalProps) => {
  const isDesktop = useIsDesktopViewport();

  const { contentCSS: modalContentCSS, ...restModalProps } = modalProps ?? {};

  const { contentCSS: fullScreenContentCSS, ...restFullScreenModalProps } =
    fullScreenModalProps ?? {};

  const ModalChildren = (
    <>
      {customHeader ? (
        customHeader
      ) : (
        <div css={[headerStyle, !title && headerStyleNoTitle]}>
          {title && (
            <Text type="headline" size="s">
              {title}
            </Text>
          )}
          <RadixUIDialogClose asChild>
            <IconButton icon={<CloseIcon />} variant="transparentPrimary" />
          </RadixUIDialogClose>
        </div>
      )}
      <ScrollArea
        css={scrollAreaRootStyle}
        viewportCSS={scrollAreaViewportStyle}
        masksColor={isDesktop ? "onOverlayBackground" : "background"}
        maskTop
        maskBottom
        masksSize="xs"
        rootDisplayAsBlock
        {...scrollAreaProps}
      >
        {children}
      </ScrollArea>
      {footerChildren && (
        <footer css={[footerStyle, footerCSS]}>{footerChildren}</footer>
      )}
    </>
  );

  if (isDesktop)
    return (
      <Modal
        {...restModalProps}
        {...rootProps}
        trigger={trigger}
        contentCSS={[contentContainerStyle, modalContentCSS]}
      >
        {ModalChildren}
      </Modal>
    );

  return (
    <FullscreenModal
      {...restFullScreenModalProps}
      {...rootProps}
      modal={false}
      trigger={trigger}
      removeOverlay={true}
      contentCSS={[contentContainerStyle, fullScreenContentCSS]}
    >
      {ModalChildren}
    </FullscreenModal>
  );
};
